import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import AdminLayout from '../../components/admin/AdminLayout';
import API from '../../utils/api';

const EMPTY = {
  title: '', slug: '', content: '', excerpt: '', coverImage: '',
  tags: '', author: 'Dreamy Bedtime Team',
  metaTitle: '', metaDescription: '', isPublished: false,
};

const slugify = (str) =>
  str.toLowerCase().trim()
    .replace(/[^\w\s-]/g, '')
    .replace(/[\s_]+/g, '-')
    .replace(/^-+|-+$/g, '');

const AdminBlogEditor = () => {
  const { id } = useParams();          // undefined = new post
  const navigate = useNavigate();
  const isNew = !id || id === 'new';

  const [form, setForm]         = useState(EMPTY);
  const [preview, setPreview]   = useState(false);
  const [saving, setSaving]     = useState(false);
  const [loading, setLoading]   = useState(!isNew);
  const [error, setError]       = useState('');
  const [slugTouched, setSlugTouched] = useState(false);

  // Load existing post
  useEffect(() => {
    if (isNew) return;
    API.get(`/admin/blog/${id}`)
      .then(res => {
        const p = res.data.post;
        setForm({
          title:          p.title,
          slug:           p.slug,
          content:        p.content,
          excerpt:        p.excerpt || '',
          coverImage:     p.coverImage || '',
          tags:           (p.tags || []).join(', '),
          author:         p.author || 'Dreamy Bedtime Team',
          metaTitle:      p.metaTitle || '',
          metaDescription: p.metaDescription || '',
          isPublished:    p.isPublished,
        });
        setSlugTouched(true); // don't auto-overwrite existing slug
      })
      .catch(() => navigate('/admin/blog'))
      .finally(() => setLoading(false));
  }, [id, isNew, navigate]);

  const set = (field, value) => {
    setForm(f => {
      const next = { ...f, [field]: value };
      // Auto-generate slug from title for new posts (until manually edited)
      if (field === 'title' && !slugTouched) {
        next.slug = slugify(value);
      }
      return next;
    });
  };

  const handleSlugChange = (e) => {
    setSlugTouched(true);
    set('slug', e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-'));
  };

  const save = async (publish) => {
    setError('');
    if (!form.title.trim()) return setError('Title is required.');
    if (!form.content.trim()) return setError('Content is required.');
    if (!form.slug.trim()) return setError('Slug is required.');

    setSaving(true);
    const payload = {
      ...form,
      tags: form.tags.split(',').map(t => t.trim()).filter(Boolean),
      isPublished: publish !== undefined ? publish : form.isPublished,
    };

    try {
      if (isNew) {
        const res = await API.post('/admin/blog', payload);
        navigate(`/admin/blog/${res.data.post._id}/edit`, { replace: true });
      } else {
        await API.put(`/admin/blog/${id}`, payload);
      }
      if (publish === true)  setForm(f => ({ ...f, isPublished: true }));
      if (publish === false) setForm(f => ({ ...f, isPublished: false }));
    } catch (err) {
      setError(err.response?.data?.message || 'Save failed.');
    }
    setSaving(false);
  };

  if (loading) return (
    <AdminLayout title="Blog Editor">
      <div className="text-center py-5"><div className="spinner-border" style={{ color: '#6f42c1' }}></div></div>
    </AdminLayout>
  );

  return (
    <AdminLayout title={isNew ? 'New Blog Post' : 'Edit Blog Post'}>
      {/* Header */}
      <div className="d-flex align-items-center justify-content-between mb-4 flex-wrap gap-2">
        <div>
          <button className="btn btn-sm btn-outline-secondary rounded-pill me-2" onClick={() => navigate('/admin/blog')}>
            <i className="bi bi-arrow-left me-1"></i>Back
          </button>
          <span className="fw-bold" style={{ color: 'var(--text-main)', fontFamily: "'Fredoka One',cursive", fontSize: '20px' }}>
            {isNew ? 'New Post' : 'Edit Post'}
          </span>
        </div>
        <div className="d-flex gap-2 flex-wrap">
          <button className="btn btn-sm btn-outline-secondary rounded-pill" onClick={() => setPreview(v => !v)}>
            <i className={`bi ${preview ? 'bi-pencil' : 'bi-eye'} me-1`}></i>
            {preview ? 'Edit' : 'Preview'}
          </button>
          {form.isPublished ? (
            <button className="btn btn-sm btn-outline-warning rounded-pill" onClick={() => save(false)} disabled={saving}>
              <i className="bi bi-eye-slash me-1"></i>Unpublish
            </button>
          ) : (
            <button className="btn btn-sm btn-outline-success rounded-pill" onClick={() => save(true)} disabled={saving}>
              <i className="bi bi-send me-1"></i>Publish
            </button>
          )}
          <button className="btn btn-sm btn-dream rounded-pill px-4" onClick={() => save()} disabled={saving}>
            {saving ? <span className="spinner-border spinner-border-sm me-1"></span> : <i className="bi bi-floppy me-1"></i>}
            Save Draft
          </button>
        </div>
      </div>

      {error && <div className="alert alert-danger rounded-3">{error}</div>}

      {form.isPublished && (
        <div className="alert alert-success d-flex align-items-center gap-2 rounded-3 py-2">
          <i className="bi bi-check-circle-fill"></i>
          Published at{' '}
          <Link to={`/blog/${form.slug}`} target="_blank" rel="noreferrer" className="fw-bold">
            /blog/{form.slug}
          </Link>
        </div>
      )}

      <div className="row g-4">
        {/* Left: form */}
        <div className="col-lg-8">
          <div className="card border-0 shadow-sm p-4" style={{ borderRadius: '14px' }}>

            {/* Title */}
            <div className="mb-3">
              <label className="form-label fw-semibold">Title <span className="text-danger">*</span></label>
              <input
                className="form-control form-control-lg"
                placeholder="e.g. 5 Best Bedtime Routines for Toddlers"
                value={form.title}
                onChange={e => set('title', e.target.value)}
                style={{ fontFamily: "'Fredoka One',cursive", fontSize: '20px' }}
              />
            </div>

            {/* Slug */}
            <div className="mb-3">
              <label className="form-label fw-semibold">URL Slug <span className="text-danger">*</span></label>
              <div className="input-group">
                <span className="input-group-text text-muted" style={{ fontSize: '13px' }}>dreamybedtime.com/blog/</span>
                <input
                  className="form-control"
                  value={form.slug}
                  onChange={handleSlugChange}
                  placeholder="my-blog-post-slug"
                />
              </div>
            </div>

            {/* Excerpt */}
            <div className="mb-3">
              <label className="form-label fw-semibold">
                Excerpt <span className="text-muted fw-normal">(shown in listing + SEO description)</span>
              </label>
              <textarea
                className="form-control"
                rows={2}
                maxLength={300}
                placeholder="A short summary — keep it under 160 chars for best SEO results."
                value={form.excerpt}
                onChange={e => set('excerpt', e.target.value)}
              />
              <small className="text-muted">{form.excerpt.length}/300</small>
            </div>

            {/* Content */}
            <div className="mb-3">
              <label className="form-label fw-semibold">
                Content <span className="text-danger">*</span>
                <span className="text-muted fw-normal ms-2" style={{ fontSize: '12px' }}>HTML is supported</span>
              </label>
              {preview ? (
                <div
                  className="border rounded-3 p-4"
                  style={{ minHeight: 400, background: 'var(--header-bg)', lineHeight: 1.8 }}
                  dangerouslySetInnerHTML={{ __html: form.content }}
                />
              ) : (
                <textarea
                  className="form-control font-monospace"
                  rows={20}
                  placeholder={'<h2>Introduction</h2>\n<p>Your content here...</p>'}
                  value={form.content}
                  onChange={e => set('content', e.target.value)}
                  style={{ fontSize: '13px', lineHeight: 1.6 }}
                />
              )}
            </div>
          </div>
        </div>

        {/* Right: metadata */}
        <div className="col-lg-4">

          {/* Post settings */}
          <div className="card border-0 shadow-sm p-4 mb-4" style={{ borderRadius: '14px' }}>
            <h6 className="fw-bold mb-3" style={{ color: 'var(--text-main)' }}>Post Settings</h6>

            <div className="mb-3">
              <label className="form-label fw-semibold" style={{ fontSize: '13px' }}>Cover Image URL</label>
              <input
                className="form-control form-control-sm"
                placeholder="https://..."
                value={form.coverImage}
                onChange={e => set('coverImage', e.target.value)}
              />
              {form.coverImage && (
                <img src={form.coverImage} alt="cover" className="mt-2 rounded-3 w-100" style={{ maxHeight: 140, objectFit: 'cover' }} />
              )}
            </div>

            <div className="mb-3">
              <label className="form-label fw-semibold" style={{ fontSize: '13px' }}>Tags <span className="text-muted fw-normal">(comma-separated)</span></label>
              <input
                className="form-control form-control-sm"
                placeholder="parenting, tips, bedtime"
                value={form.tags}
                onChange={e => set('tags', e.target.value)}
              />
            </div>

            <div className="mb-0">
              <label className="form-label fw-semibold" style={{ fontSize: '13px' }}>Author</label>
              <input
                className="form-control form-control-sm"
                value={form.author}
                onChange={e => set('author', e.target.value)}
              />
            </div>
          </div>

          {/* SEO settings */}
          <div className="card border-0 shadow-sm p-4" style={{ borderRadius: '14px' }}>
            <h6 className="fw-bold mb-1" style={{ color: 'var(--text-main)' }}>SEO Settings</h6>
            <p className="text-muted mb-3" style={{ fontSize: '12px' }}>Leave blank to use title/excerpt automatically.</p>

            <div className="mb-3">
              <label className="form-label fw-semibold" style={{ fontSize: '13px' }}>Meta Title</label>
              <input
                className="form-control form-control-sm"
                maxLength={60}
                placeholder={form.title || 'Post title'}
                value={form.metaTitle}
                onChange={e => set('metaTitle', e.target.value)}
              />
              <small className="text-muted">{(form.metaTitle || form.title).length}/60</small>
            </div>

            <div className="mb-0">
              <label className="form-label fw-semibold" style={{ fontSize: '13px' }}>Meta Description</label>
              <textarea
                className="form-control form-control-sm"
                rows={3}
                maxLength={160}
                placeholder={form.excerpt || 'Post excerpt'}
                value={form.metaDescription}
                onChange={e => set('metaDescription', e.target.value)}
              />
              <small className="text-muted">{(form.metaDescription || form.excerpt).length}/160</small>
            </div>

            {/* Google preview */}
            <div className="mt-3 p-3 rounded-3" style={{ background: 'var(--header-bg)', border: '1px solid #e9ecef' }}>
              <div style={{ fontSize: '11px', color: 'var(--text-soft)', marginBottom: '4px' }}>Google preview</div>
              <div style={{ color: '#1a0dab', fontSize: '16px', fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {form.metaTitle || form.title || 'Post Title'}
              </div>
              <div style={{ color: '#006621', fontSize: '12px' }}>dreamybedtime.com/blog/{form.slug || 'slug'}</div>
              <div style={{ color: '#545454', fontSize: '13px', lineHeight: 1.4, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                {form.metaDescription || form.excerpt || 'Post excerpt will appear here…'}
              </div>
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminBlogEditor;
